<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import type ButtonWithSpinnerType from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import CheckBox from "@/components/forms/SharedComponents/DarkBlueInputs/Checkbox.vue";
import { channelTypes } from "@/components/program-manager/sessions/constants";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import { hasStripeApplicationDepositEnabled, useCustomPriceFees } from "@/composables/authorization.js";
import { useNotifications } from "@/composables/useNotifications";
import { useTenantTheming } from "@/composables/useTenantTheming";
import { INSTITUTION_APPLICATION_DEPOSIT_VALUE } from "@/constants";
import { getCategoryItemArray, trackPurchase } from "@/util/eventTracker.js";
import { specialPricingOverrideEntityId } from "@/util/specialPricingOverride";
import type { DepositModelData } from "../../../composables/types/deposit";
import { useDepositModels } from "../../../composables/useDepositModels";
import { CHECKOUT_SUBMIT_BUTTON_LABELS } from "../constants";
import type { GoogleAddress } from "../types/address";
import type { FormIoApplicationSubmission } from "../types/formio";
import type { PaymentServicePayload } from "../types/paymentService";
import type { AppliedBillingAgreement } from "./types";

const REQUIRED_ERROR_COPY: string = "This field is required.";
const FORM_IO_FORM: string = "institutionpayment";

const { getPaymentServicePayload, getSubmissionsDataForFormIo } = useDepositModels();

const store = useStore();
const { secondaryColorClassLinks } = useTenantTheming();

const props = defineProps({
  isReadOnly: {
    type: Boolean,
    default: false,
  },
  existingPaymentData: {
    type: Object,
    default() {
      return {};
    },
  },
  initialCountries: {
    type: Array,
    default: () => [],
  },
  buttonLabel: {
    type: String,
    default: CHECKOUT_SUBMIT_BUTTON_LABELS.application,
  },
  application: {
    type: Object,
    default() {
      return {};
    },
  },
});

const vuelidateState = reactive({
  cancellationPolicyCheckbox: {
    policy: false,
  },
});

const vuelidateRules = computed(() => ({
  cancellationPolicyCheckbox: {
    policy: {
      accepted: helpers.withMessage(
        REQUIRED_ERROR_COPY,
        (value) => props.isReadOnly || !requiresCancellationPolicyAgreement.value || value === true,
      ),
    },
  },
}));

const vuelidate = useVuelidate(vuelidateRules, vuelidateState);

const hasErrors = ref<boolean>(false);
const isComponentLoading = ref<boolean>(false);
const paymentSubmitButtonRef = ref<typeof ButtonWithSpinnerType | undefined>();
const submissionId = ref<string | undefined>();

const { sendNotifications } = useNotifications();

const emit = defineEmits(["next-step", "update-order"]);

const currentUser = computed(() => store.getters.getCurrentUser);
const ownerId = computed(() => currentUser.value.formioStudentId || currentUser.value.formioAdvisorId);
const participantId = computed(() => store.state.currentUser?.participantId);
const customerId = computed(() => store.state.customer_id);
const appliedAgreement = computed<AppliedBillingAgreement | undefined>(() => {
  return props.application.appliedAgreement?.value;
});

const { registerLoadingState } = props.application;

const APPLICATION_LOADER = "institution-pays";

registerLoadingState(APPLICATION_LOADER, isComponentLoading);

const hostInstitution = computed(
  () => props.application.session.value.session_hosting_institutions?.[0]?.entity?.name,
);

const homeInstitution = computed(() => props.application?.homeInstitution?.value?.name);

const sessionApplicationFee = props.application.sessionApplicationFee;
const sessionFeesBlockingCheckout = props.application.sessionFeesBlockingCheckout;
const withdrawalPolicyUrl = props.application.withdrawalPolicyUrl;
const requiresCancellationPolicyAgreement = props.application.requiresCancellationPolicyAgreement;

const institutionApplicationFeeAmountDollars = computed(() => {
  const feeCents = sessionApplicationFee.cents.value;
  const feeCentsAsNumber = Number(feeCents);

  const hasValidSessionFee =
    useCustomPriceFees.value === true &&
    feeCents !== null &&
    feeCents !== undefined &&
    !Number.isNaN(feeCentsAsNumber);

  if (hasValidSessionFee) {
    return Number(sessionApplicationFee.dollars.value);
  }

  return INSTITUTION_APPLICATION_DEPOSIT_VALUE;
});

// Chooses which layout to render, not whether the cancellation policy applies.
const isCustomizedSession = computed<boolean>(() => {
  return props.application.session.value?.channel_id === channelTypes.CUSTOM;
});

const transactionId = computed<string>(() => {
  return `institution_pays_${props.application.applicationId.value}`;
});

const BLANK_ADDRESS: GoogleAddress = {
  street_address_1: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
};

const currentAddress = computed<GoogleAddress>(() => {
  const profile = props.application.profile.value;
  if (!profile) return BLANK_ADDRESS;
  return {
    street_address_1: profile.current_address_line_1,
    city: profile.current_address_city,
    state: profile.current_address_state,
    country: profile.current_address_country_id,
    postal_code: profile.current_address_postal_code,
  };
});

const permanentAddress = computed<GoogleAddress>(() => {
  const profile = props.application.profile.value;
  if (!profile) return BLANK_ADDRESS;
  return {
    street_address_1: profile.permanent_address_line_1,
    city: profile.permanent_address_city,
    state: profile.permanent_address_state,
    country: profile.permanent_address_country_id,
    postal_code: profile.permanent_address_postal_code,
  };
});

const billingAddress = computed<GoogleAddress>(() => {
  const profile = props.application.profile.value;
  if (!profile) return BLANK_ADDRESS;
  return {
    street_address_1: profile.billing_address_line_1,
    city: profile.billing_address_city,
    state: profile.billing_address_state,
    country: profile.billing_address_country_id,
    postal_code: profile.billing_address_postal_code,
  };
});

const depositPayloadData = computed<DepositModelData>(() => {
  return {
    person: {
      first_name: props.application.userData.value.data.firstname,
      last_name: props.application.userData.value.data.lastname,
    },
    applicationId: props.application.applicationId.value,
    amount: institutionApplicationFeeAmountDollars.value,
    productName: "Application Deposit",
    session: props.application.session.value,
    userData: props.application.userData.value,
    profile: props.application.profile.value,
    ipeds: props.application.ipeds.value,
    ownerId: ownerId.value,
    billingAddress: billingAddress.value,
    currentAddress: currentAddress.value,
    permanentAddress: permanentAddress.value,
    customerId: customerId.value,
    hostInstitution: hostInstitution.value,
    contractNumber: appliedAgreement.value?.contract_number ?? null,
    participantId: participantId.value,
    cancellationPolicy: requiresCancellationPolicyAgreement.value
      ? vuelidateState.cancellationPolicyCheckbox.policy
      : true,
    special_pricing_override_entity_id: specialPricingOverrideEntityId(),
  };
});

const submissionsDataForFormIo = computed<FormIoApplicationSubmission>(() => {
  const data = getSubmissionsDataForFormIo(depositPayloadData.value);
  if (props.existingPaymentData?._id) {
    data._id = props.existingPaymentData._id;
  }
  return data;
});

const paymentServicePayload = computed<PaymentServicePayload>(() => {
  return getPaymentServicePayload(depositPayloadData.value);
});

const sendPurchaseEvent = () => {
  if (!props.application.orderPricing.value) return;
  const categories = ["Housing", "Internship", "Events", "Additional Fees", "Classes Academic Fees"];

  categories.forEach((category) => {
    const itemsArrays = getCategoryItemArray(
      category,
      props.application.orderPricing.value,
      props.application.primaryOrder.value,
      props.application.session.value,
    );
    if (itemsArrays && itemsArrays.length > 0) {
      itemsArrays.forEach((item) => {
        trackPurchase(transactionId.value, institutionApplicationFeeAmountDollars.value, "USD", "not_set", [item]);
      });
    }
  });
};

const submitInstitutionPays = async () => {
  isComponentLoading.value = true;
  hasErrors.value = false;

  paymentSubmitButtonRef.value?.startLoading();
  if (props.isReadOnly) {
    emit("next-step");
    isComponentLoading.value = false;
    return;
  }
  try {
    const hasSubmissionErrors = await props.application.validateSubmission({
      formPath: FORM_IO_FORM,
      body: submissionsDataForFormIo.value,
    });
    if (!hasSubmissionErrors) {
      //Prevent duplicate submissions on Formio and Workato
      if (!submissionId.value) {
        if (hasStripeApplicationDepositEnabled.value) {
          await props.application.submitToPaymentService(paymentServicePayload.value);
        }

        const response = await props.application.saveApplicationSubmission(
          FORM_IO_FORM,
          submissionsDataForFormIo.value,
        );

        if (response) {
          submissionId.value = response;
          sendNotifications("submit", props.application.session.value.salesforce_id);
        }
        sendPurchaseEvent();
        emit("update-order");
        isComponentLoading.value = false;
      }
    }
  } catch (error) {
    hasErrors.value = true;
    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags: {
          troubleshooting_note: "Submitting application institution pays deposit failed.",
          application_id: props.application.applicationId.value,
        },
      });
    }
    paymentSubmitButtonRef.value?.stopLoading();
    emit("next-step");
    isComponentLoading.value = false;
  }
};

const withdrawalPolicyCopy = computed<string>(() => {
  if (props.application.session.value?.requires_application) {
    return "To finalize your application, please acknowledge the withdrawal policy and click submit.";
  } else {
    return "To finalize your registration, please acknowledge the withdrawal policy and click submit.";
  }
});
</script>
<template>
  <section>
    <div class="grid grid-cols-1 gap-y-7 md:pt-10 pt-4">
      <div v-if="isReadOnly">
        <p class="font-bold text-2xl">
          Let's take the next step!
        </p>
        <p>
          To participate in your program, you'll need to complete a few required To Dos. Click Continue to get started
          and keep an eye out for the form deadlines so that you submit everything on time!
        </p>
      </div>
      <div v-if="!isReadOnly && isCustomizedSession" class="font-medium text-sm text-left sm:text-center">
        <h1 class="font-bold text-2xl" data-testid="custom-header">
          Let's take the next step!
        </h1>
        <p class="mt-10" data-testid="custom-paragraph-1">
          To participate in your program, you'll need to complete a few required To Dos. Click Continue to get started
          and keep an eye out for the form deadlines so that you submit everything on time!
        </p>
      </div>
      <template v-else-if="!isReadOnly && !isCustomizedSession">
        <!-- The acknowledgment carries the only (*) field in this step. -->
        <p v-if="requiresCancellationPolicyAgreement" class="font-medium text-xs">
          Fields marked with (*) are required.
        </p>
        <p class="font-bold text-2xl">Good news! {{ homeInstitution }} is covering your application fee!</p>
        <!-- Withdrawal Policy -->
        <template v-if="requiresCancellationPolicyAgreement">
          <p>
            {{ withdrawalPolicyCopy }}
          </p>
          <check-box
            id="cancellationPolicy"
            data-testid="cancellationPolicyCheckbox"
            v-model.trim="vuelidate.cancellationPolicyCheckbox.policy.$model"
            :vuelidate-field="vuelidate.cancellationPolicyCheckbox.policy"
            :is-disabled="isReadOnly"
          >
            <template #label>
              I agree to the
              <a
                class="underline"
                :class="secondaryColorClassLinks"
                :href="withdrawalPolicyUrl"
                target="_blank"
                >Withdrawal Policy.</a
              >*
            </template>
          </check-box>
        </template>
      </template>

      <div class="md:mt-10 block">
        <button-with-spinner
          id="paymentSubmitButton"
          ref="paymentSubmitButtonRef"
          data-testid="paymentSubmitButton"
          type="submit"
          variant="secondary"
          variant-type="block"
          button-height="min-h-[58px] md:min-h-[60px] mt-4"
          :grey-disabled-class="true"
          :disabled="vuelidate.$invalid || sessionFeesBlockingCheckout"
          @click="submitInstitutionPays"
        >
          <div class="flex items-center justify-center">
            <span class="pr-3 uppercase">
              {{ buttonLabel }}
            </span>
            <ArrowRight custom-class="w-4 h-4" />
          </div>
        </button-with-spinner>
        <p v-if="hasErrors" class="text-error-900">
          Please try again. If this problem persists, please contact productsupport@apiexperience.com.
        </p>
      </div>
    </div>
  </section>
</template>
